import "core-js/modules/es.iterator.constructor.js";
import "core-js/modules/es.iterator.map.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Element, List, Text, useTokens } from '@klarna/ui';
import { useQuery } from '@tanstack/react-query';
import { PriceRunnerLogo } from 'features/fabric/components/PriceRunnerLogo';
import RichTextMarkdown from 'features/richText/components/richTextMarkdown/RichTextMarkdown';
import { getHideContent } from 'features/searchCompare/state/menu';
import { buildLinkProps } from 'features/searchCompare/utils/footerUtils';
import { useTextPlaceholders } from 'features/textPlaceholders/useTextPlaceholders';
import { withTextPlaceholders } from 'features/textPlaceholders/withTextPlaceholders';
import { getSiteSetting } from 'framework/globalStorage/settingsStore';
import { useAppSelector } from 'framework/store/hooks';
import { getCountryCode, getLocale } from 'framework/utils/core/siteUtils';
import { useTranslate } from 'framework/utils/i18n/useTranslate';
import { Fragment } from 'react';
import { useLocation } from 'react-router';
import Footer from './components/Footer';
import SocialLink from './components/socialLink/SocialLink';
import { footerQuery } from './footerQuery';
import getInitialProps from './getInitialProps';
const MOBILE_PL_MENU_HEIGHT = 73;
const MAIN_AREA_COLUMNS = {
    default: 12,
    '@breakpoint[l]': 10
};
const FIRST_COLUMN_SPAN = {
    default: 12,
    '@breakpoint[l]': 4
};
const REST_COLUMNS_SPAN = {
    default: 12,
    '@breakpoint[m]': 4,
    '@breakpoint[l]': 2
};
// Below `l` the link columns come first and the logo column last; from `l` the logo leads.
const LINK_COLUMN_ORDER = {
    default: 1,
    '@breakpoint[l]': 2
};
const LOGO_COLUMN_ORDER = {
    default: 4,
    '@breakpoint[l]': 1
};
const COPYRIGHT_YEAR = new Date().getFullYear();
const isInvalid = (link)=>!(link === null || link === void 0 ? void 0 : link.url);
const getSocialNetwork = (name)=>{
    if (name === 'facebook-outlined') {
        return 'facebook';
    } else if (name === 'linkedin-outlined') {
        return 'linkedin';
    }
    return undefined;
};
const useFooter = ()=>{
    const countryCode = getCountryCode();
    const locale = getLocale();
    const textPlaceholders = useTextPlaceholders();
    const { data: footerData } = useQuery(footerQuery(countryCode, locale));
    return withTextPlaceholders(footerData, textPlaceholders);
};
const FooterWrapper = ({ children, display })=>/*#__PURE__*/ _jsx(Element, {
        display: display,
        order: 2,
        width: "100%",
        children: children
    });
const FooterContainer = ()=>{
    var _location_pathname;
    const translate = useTranslate();
    const location = useLocation();
    const countryCode = getCountryCode();
    const hideContent = useAppSelector(getHideContent);
    const tokens = useTokens();
    const footer = useFooter();
    // We don't check isLoading or isError here, we just always render
    // the fallback footer when footerData is not available for any reason
    const headings = footer === null || footer === void 0 ? void 0 : footer.headings;
    const contentLinks = footer === null || footer === void 0 ? void 0 : footer.contentLinks;
    const socialLinks = footer === null || footer === void 0 ? void 0 : footer.socialLinks;
    const countryLinks = footer === null || footer === void 0 ? void 0 : footer.countryLinks;
    const copyrightAddition = translate('PR.Footer.CopyrightAddition');
    const footerDescriptionKey = `PR.Footer.Description.${countryCode}`;
    const copyrightText = `Copyright 1999-${COPYRIGHT_YEAR} Klarna Technologies AB ${copyrightAddition ? `\n ${copyrightAddition}` : ''}`;
    const wrapperDisplay = hideContent ? 'none' : undefined;
    if (!footer) {
        const supportEmail = getSiteSetting('support_email');
        return /*#__PURE__*/ _jsx(FooterWrapper, {
            display: wrapperDisplay,
            children: /*#__PURE__*/ _jsx(Footer, {
                children: /*#__PURE__*/ _jsx(Footer.Bottom, {
                    copyright: copyrightText,
                    children: /*#__PURE__*/ _jsx(Footer.Link, {
                        href: `mailto:${supportEmail}`,
                        children: supportEmail
                    })
                })
            })
        });
    }
    return /*#__PURE__*/ _jsx(FooterWrapper, {
        display: wrapperDisplay,
        children: /*#__PURE__*/ _jsxs(Footer, {
            children: [
                /*#__PURE__*/ _jsxs(Footer.Main, {
                    columns: MAIN_AREA_COLUMNS,
                    children: [
                        contentLinks === null || contentLinks === void 0 ? void 0 : contentLinks.map((item, index)=>{
                            var _headings_index;
                            if (!item) {
                                return null;
                            }
                            const heading = (headings === null || headings === void 0 ? void 0 : (_headings_index = headings[index]) === null || _headings_index === void 0 ? void 0 : _headings_index.content) || '';
                            const list = item === null || item === void 0 ? void 0 : item.list;
                            return /*#__PURE__*/ _jsx(Footer.Column, {
                                heading: heading,
                                order: LINK_COLUMN_ORDER,
                                span: REST_COLUMNS_SPAN,
                                children: list && /*#__PURE__*/ _jsx(List, {
                                    gap: "space/8",
                                    children: list.map((link)=>{
                                        if (!link || isInvalid(link)) {
                                            return null;
                                        }
                                        const linkProps = buildLinkProps(link);
                                        const linkTitle = link === null || link === void 0 ? void 0 : link.title;
                                        return /*#__PURE__*/ _jsx(List.Item, {
                                            children: /*#__PURE__*/ _jsx(Footer.Link, {
                                                href: linkProps.to,
                                                rel: linkProps.rel,
                                                target: linkProps.target,
                                                children: linkTitle
                                            })
                                        }, `key-${linkTitle}`);
                                    })
                                })
                            }, item.id);
                        }),
                        /*#__PURE__*/ _jsx(Footer.Column, {
                            order: LOGO_COLUMN_ORDER,
                            span: FIRST_COLUMN_SPAN,
                            children: /*#__PURE__*/ _jsxs(Element, {
                                paddingRight: {
                                    default: 0,
                                    '@breakpoint[m]': 'space/40'
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(Element, {
                                        paddingBottom: {
                                            default: 'space/16',
                                            '@breakpoint[m]': 'space/24'
                                        },
                                        children: /*#__PURE__*/ _jsx(Element, {
                                            paddingVertical: "space/12",
                                            children: /*#__PURE__*/ _jsx(PriceRunnerLogo, {
                                                color: tokens.colors['text/inverse'],
                                                width: 240
                                            })
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(RichTextMarkdown, {
                                        source: translate(footerDescriptionKey)
                                    }),
                                    countryLinks && /*#__PURE__*/ _jsx(Text, {
                                        color: "text/inverse",
                                        textToken: "body/m",
                                        children: countryLinks.map((country, index)=>{
                                            if (!country) {
                                                return null;
                                            }
                                            const countryName = country === null || country === void 0 ? void 0 : country.title;
                                            const linkProps = buildLinkProps(country);
                                            return /*#__PURE__*/ _jsxs(Fragment, {
                                                children: [
                                                    index !== 0 && /*#__PURE__*/ _jsx(Text, {
                                                        tag: "span",
                                                        children: ", "
                                                    }),
                                                    /*#__PURE__*/ _jsx(Footer.Link, {
                                                        inline: true,
                                                        href: linkProps.to,
                                                        rel: linkProps.rel,
                                                        target: linkProps.target,
                                                        children: countryName
                                                    })
                                                ]
                                            }, `key-${countryName}`);
                                        })
                                    })
                                ]
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Footer.Bottom, {
                    copyright: copyrightText,
                    children: socialLinks && socialLinks.map((link)=>{
                        if (isInvalid(link)) {
                            return null;
                        }
                        const linkProps = buildLinkProps(link);
                        const linkTitle = link === null || link === void 0 ? void 0 : link.title;
                        const network = getSocialNetwork(linkTitle);
                        let ariaLabel = '';
                        if (linkTitle === 'facebook-outlined') {
                            ariaLabel = translate('PR.Footer.GO_TO_FACEBOOK');
                        } else if (linkTitle === 'linkedin-outlined') {
                            ariaLabel = translate('PR.Footer.GO_TO_LINKEDIN');
                        }
                        if (!network) {
                            return null;
                        }
                        return /*#__PURE__*/ _jsx(SocialLink, {
                            href: linkProps.to,
                            label: ariaLabel,
                            network: network,
                            rel: linkProps.rel,
                            target: linkProps.target
                        }, `key-${linkTitle}`);
                    })
                }),
                (location === null || location === void 0 ? void 0 : (_location_pathname = location.pathname) === null || _location_pathname === void 0 ? void 0 : _location_pathname.startsWith('/pl')) && // Room for the sticky product list menu, which is only shown below `l`.
                /*#__PURE__*/ _jsx(Element, {
                    display: {
                        default: 'flex',
                        '@breakpoint[l]': 'none'
                    },
                    height: MOBILE_PL_MENU_HEIGHT
                })
            ]
        })
    });
};
FooterContainer.getInitialProps = getInitialProps;
export default FooterContainer;
